import React, { Suspense, lazy, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
    LayoutDashboard, ShoppingBag, Package, Boxes, Tag, Users, Truck,
    Image as ImgIcon, FileText, Settings as Cog, Shield, LogOut,
    Home, BookOpen, Stethoscope, Mail, Cloud, Bot, Inbox,
    Menu, Rocket, Sparkles, Layers, Star, X, Gift,
} from "lucide-react";

// Lazy-loaded tab components — each tab is code-split so the admin page
// only downloads the bundle for the tab the user actually opens.
const Dashboard       = lazy(() => import("./admin/Dashboard"));
const Orders          = lazy(() => import("./admin/Orders"));
const Products        = lazy(() => import("./admin/Products"));
const Inventory       = lazy(() => import("./admin/Inventory"));
const Coupons         = lazy(() => import("./admin/Coupons"));
const CouponClaims    = lazy(() => import("./admin/CouponClaims"));
const Customers       = lazy(() => import("./admin/Customers"));
const Consultations   = lazy(() => import("./admin/Consultations"));
const Doctors         = lazy(() => import("./admin/Doctors"));
const CoreTeam        = lazy(() => import("./admin/CoreTeam"));
const Reviews         = lazy(() => import("./admin/Reviews"));
const Articles        = lazy(() => import("./admin/Articles"));
const Offers          = lazy(() => import("./admin/Offers"));
const Vendors         = lazy(() => import("./admin/Vendors"));
const Media           = lazy(() => import("./admin/Media"));
const Compliance      = lazy(() => import("./admin/Compliance"));
const StoreSettings   = lazy(() => import("./admin/StoreSettings"));
const RazorpayTab     = lazy(() => import("./admin/Razorpay"));
const GoKwikSettings  = lazy(() => import("./admin/GoKwikSettings"));
const CloudinarySettings = lazy(() => import("./admin/CloudinarySettings"));
const DelhiverySettings  = lazy(() => import("./admin/DelhiverySettings"));
const SmtpSettings    = lazy(() => import("./admin/SmtpSettings"));
const Security        = lazy(() => import("./admin/Security"));
const ChatbotSettings = lazy(() => import("./admin/ChatbotSettings"));
const SitePages       = lazy(() => import("./admin/Pages"));
const ChatInbox       = lazy(() => import("./admin/ChatInbox"));

const TABS = [
    { id: "dashboard",     label: "Dashboard",          icon: LayoutDashboard, comp: Dashboard },
    { id: "orders",        label: "Orders",             icon: ShoppingBag,     comp: Orders },
    { id: "products",      label: "Products",           icon: Package,         comp: Products },
    { id: "inventory",     label: "Inventory",          icon: Boxes,           comp: Inventory },
    { id: "coupons",       label: "Coupons",            icon: Tag,             comp: Coupons },
    { id: "coupon-claims", label: "Coupon Claims",      icon: Sparkles,        comp: CouponClaims },
    { id: "customers",     label: "Customers",          icon: Users,           comp: Customers },
    { id: "consultations", label: "Consultations",      icon: Stethoscope,     comp: Consultations },
    { id: "core-team",     label: "Board / Core Team",  icon: Users,           comp: CoreTeam },
    { id: "doctors",       label: "Consult Doctors",    icon: Stethoscope,     comp: Doctors },
    { id: "reviews",       label: "Reviews",            icon: Star,            comp: Reviews },
    { id: "articles",      label: "Articles (Learn)",   icon: BookOpen,        comp: Articles },
    { id: "offers",        label: "Offers / Banners",   icon: Gift,            comp: Offers },
    { id: "chatbot",       label: "Chatbot AI",         icon: Bot,             comp: ChatbotSettings },
    { id: "chat-inbox",    label: "Chat Inbox",         icon: Inbox,           comp: ChatInbox },
    { id: "pages",         label: "Site Pages",         icon: FileText,        comp: SitePages },
    { id: "vendors",       label: "Vendors",            icon: Truck,           comp: Vendors },
    { id: "media",         label: "Media Library",      icon: ImgIcon,         comp: Media },
    { id: "compliance",    label: "Legal & Compliance", icon: FileText,        comp: Compliance },
    { id: "settings",      label: "Store Settings",     icon: Cog,             comp: StoreSettings },
    { id: "razorpay",      label: "Razorpay Gateway",   icon: Cog,             comp: RazorpayTab },
    { id: "gokwik",        label: "GoKwik Checkout",    icon: Layers,          comp: GoKwikSettings },
    { id: "cloudinary",    label: "Cloudinary",         icon: Cloud,           comp: CloudinarySettings },
    { id: "delhivery",     label: "Delhivery",          icon: Rocket,          comp: DelhiverySettings },
    { id: "smtp",          label: "Email (SMTP)",       icon: Mail,            comp: SmtpSettings },
    { id: "security",      label: "Admin Security",     icon: Shield,          comp: Security },
];

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("dashboard");
    const [toast, setToast] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    if (loading) return <div className="admin-shell grid place-items-center">Loading…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2200);
    };
    const selectTab = (id) => { setTab(id); setSidebarOpen(false); };

    const ActiveTab = (TABS.find((t) => t.id === tab) || TABS[0]).comp;

    return (
        <div className="admin-shell flex flex-col lg:flex-row" data-testid="admin-panel">
            {/* Mobile top bar */}
            <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#25382e] bg-[#0f1a14] sticky top-0 z-30">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[var(--gold)] grid place-items-center text-[#0f1a14] text-sm font-bold">U</div>
                    <div className="font-serif text-[16px] text-[#e9e2d3]">UCare Admin</div>
                </div>
                <button onClick={() => setSidebarOpen(true)} className="text-[#e9e2d3] p-2" data-testid="admin-open-sidebar">
                    <Menu size={20} />
                </button>
            </div>

            {/* Sidebar (drawer on mobile) */}
            {sidebarOpen && (
                <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setSidebarOpen(false)} data-testid="admin-sidebar-backdrop" />
            )}
            <aside
                className={`w-64 shrink-0 border-r border-[#25382e] p-4 bg-[#0f1a14] overflow-y-auto z-50
                    fixed top-0 bottom-0 left-0 transition-transform lg:relative lg:sticky lg:top-0 lg:h-screen lg:translate-x-0
                    ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="admin-sidebar"
            >
                <div className="flex items-center justify-between mb-8 px-2 pt-2">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[var(--gold)] grid place-items-center text-[#0f1a14]">U</div>
                        <div>
                            <div className="font-serif text-[18px] text-[#e9e2d3]">UCare</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#b8b0a0]">Admin Console</div>
                        </div>
                    </div>
                    <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-[#b8b0a0] p-1" data-testid="admin-close-sidebar">
                        <X size={18} />
                    </button>
                </div>
                <nav className="space-y-1">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => selectTab(t.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition ${
                                tab === t.id ? "bg-[var(--gold)] text-[#0f1a14] font-semibold" : "text-[#b8b0a0] hover:bg-[#1f2e26] hover:text-[#e9e2d3]"
                            }`}
                            data-testid={`admin-tab-${t.id}`}
                        >
                            <t.icon size={15} />
                            <span>{t.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="border-t border-[#25382e] mt-6 pt-4 space-y-1">
                    <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-[#b8b0a0] hover:bg-[#1f2e26] hover:text-[#e9e2d3]" data-testid="admin-go-store">
                        <Home size={15} /> View Store
                    </button>
                    <button onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-[#b8b0a0] hover:bg-[#1f2e26] hover:text-[#e9e2d3]" data-testid="admin-logout">
                        <LogOut size={15} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 p-4 sm:p-6 lg:p-10 min-w-0">
                <Suspense fallback={<div className="text-[#b8b0a0]">Loading tab…</div>}>
                    <ActiveTab onToast={showToast} />
                </Suspense>
            </main>

            {toast && <div className="toast" data-testid="admin-toast">{toast}</div>}
        </div>
    );
}
